import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "wouter";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Download,
  FileText,
  Image as ImageIcon,
  Info,
  Layout as LayoutIcon,
  Loader2,
  Lock,
  PenLine,
  Plus,
  Presentation,
  Sparkles,
  Trash2,
  Upload,
  UserSquare2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import NextStepFooter from "@/components/NextStepFooter";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { Input } from "@/components/ui/Inputs";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import DeadlineBanner from "@/components/DeadlineBanner";
import { useProject, useUpdateProject } from "@/hooks/useProjects";
import {
  useSections,
  useCreateSection,
  useDeleteSection,
  useSuggestOutline,
  useApplyOutline,
  useProjectCoverage,
} from "@/hooks/useSections";
import {
  api,
  type OutlineSuggestion,
  type ProposalSection,
  type SectionKind,
  type SubmissionFormat,
} from "@/lib/api";

// ---------------------------------------------------------------------------
// Per-kind metadata: icon, label, short description, "what to do" hint.
// One source of truth so the kind picker, the rows, and the section detail
// page all read the same labels.
// ---------------------------------------------------------------------------

interface KindMeta {
  label: string;
  icon: React.ElementType;
  blurb: string;
  /** Short noun phrase describing what populates this section. */
  fills: string;
}

const KIND_META: Record<SectionKind, KindMeta> = {
  prose_ai: {
    label: "AI-drafted prose",
    icon: PenLine,
    blurb: "Narrative the team writes inside Callaloo with AI assist",
    fills: "Generated prose",
  },
  prose_external: {
    label: "External prose",
    icon: Upload,
    blurb: "Prose written elsewhere (Word/Google Docs) and uploaded",
    fills: "Uploaded document",
  },
  designer_asset: {
    label: "Designer asset",
    icon: ImageIcon,
    blurb: "Visual produced in Figma/Adobe and uploaded as PDF or PNG",
    fills: "Uploaded asset",
  },
  template: {
    label: "Templated",
    icon: LayoutIcon,
    blurb: "Auto-populated from agency-level data (company info, references)",
    fills: "Pulled from About Us",
  },
  personnel: {
    label: "CV / Personnel",
    icon: UserSquare2,
    blurb: "Tailored CVs / team profiles in the RFP's required format",
    fills: "Generated from team profiles",
  },
  pricing: {
    label: "Pricing",
    icon: Lock,
    blurb: "Sensitive financial section — restricted by default",
    fills: "Secure spreadsheet or attachment",
  },
  attachment: {
    label: "Attachment",
    icon: FileText,
    blurb: "Pre-existing required document (insurance, certifications)",
    fills: "Linked from Attachments",
  },
  compliance: {
    label: "Compliance",
    icon: CheckCircle2,
    blurb: "Pointer to compliance items in the Requirements Tracker",
    fills: "Tracked separately",
  },
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Drafts() {
  const params = useParams<{ id: string }>();
  const projectId = params.id;

  const project = useProject(projectId);
  const updateProject = useUpdateProject();
  const sections = useSections(projectId);
  const createSection = useCreateSection(projectId);
  const deleteSection = useDeleteSection(projectId);
  const suggestOutline = useSuggestOutline(projectId);
  const applyOutline = useApplyOutline(projectId);
  const coverage = useProjectCoverage(projectId);

  const [showFormatPicker, setShowFormatPicker] = useState(false);
  const [showOutlineModal, setShowOutlineModal] = useState(false);
  const [outlineProposals, setOutlineProposals] = useState<OutlineSuggestion[] | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ProposalSection | null>(null);

  // DOCX export. Uses the existing /v1/projects/:id/export endpoint, which
  // returns a presigned download URL. We trigger a download by opening the
  // URL in a new tab (Cloudflare R2 serves it with content-disposition).
  const exportMut = useMutation({
    mutationFn: (): Promise<{ fileId: string; downloadUrl: string }> =>
      api.exportProject(projectId, "docx"),
    onSuccess(result) {
      window.open(result.downloadUrl, "_blank", "noopener");
      toast.success("Export ready — opened in a new tab.");
    },
    onError(err) {
      toast.error((err as Error).message);
    },
  });

  const submissionFormat = project.data?.submissionFormat ?? null;
  // First-time landing: format unset → render the format picker as the WHOLE
  // page, not a dismissable modal. The user can't bypass; choosing a format
  // is the gate to everything else here.
  const needsFormatChoice = Boolean(project.data) && !submissionFormat;

  const handleFormatPick = async (format: SubmissionFormat) => {
    if (!projectId) return;
    try {
      await updateProject.mutateAsync({
        id: projectId,
        body: { submissionFormat: format },
      });
      setShowFormatPicker(false);
      toast.success(`Submission format set to ${formatLabel(format)}.`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleSuggestOutline = async () => {
    setShowOutlineModal(true);
    setOutlineProposals(null);
    try {
      const result = await suggestOutline.mutateAsync();
      setOutlineProposals(result.sections);
    } catch (err) {
      toast.error((err as Error).message);
      setShowOutlineModal(false);
    }
  };

  const handleApplyOutline = async (
    chosen: Array<{ title: string; kind: SectionKind; purpose?: string }>,
  ) => {
    try {
      const result = await applyOutline.mutateAsync(chosen);
      const count = result.sections?.length ?? 0;
      toast.success(
        `Approved outline — ${count} section${count === 1 ? "" : "s"} added.`,
      );
      setShowOutlineModal(false);
      setOutlineProposals(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  if (project.isLoading || sections.isLoading) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="RFP Response" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  // Gating screen: until the bid lead picks a format, the page IS the picker.
  // No skip button, no dismissable modal — the rest of Drafts depends on
  // knowing whether we're shaping a doc or a deck.
  if (needsFormatChoice) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="RFP Response" subtitle="Pick a submission format to begin" />
        <FormatChoicePage onPick={handleFormatPick} submitting={updateProject.isPending} />
      </Layout>
    );
  }

  const items = sections.data ?? [];
  const completed = items.filter((s) => s.status === "complete").length;
  const total = items.length;

  // Composition readiness — what's missing across the whole response.
  const missingDraft = items.filter(
    (s) =>
      (s.kind === "prose_ai" || s.kind === "prose_external") &&
      (!s.draftText || s.draftText.trim().length < 20),
  ).length;
  const missingAsset = items.filter(
    (s) =>
      (s.kind === "designer_asset" ||
        s.kind === "attachment" ||
        s.kind === "prose_external" ||
        s.kind === "pricing") &&
      !s.assetFileId,
  ).length;
  const readyForExport = total > 0 && missingDraft === 0;

  return (
    <Layout projectId={projectId}>
      {project.data && <DeadlineBanner project={project.data} />}
      <PageHeader
        title="RFP Response"
        subtitle={
          total === 0
            ? "Plan and write the response — section by section"
            : `${completed} of ${total} section${total === 1 ? "" : "s"} complete`
        }
        actions={
          <div className="flex items-center gap-2">
            <SubmissionFormatBadge
              format={submissionFormat}
              onChange={() => setShowFormatPicker(true)}
            />
            <Button
              variant="primary"
              size="sm"
              icon={<Download size={14} />}
              onClick={() => exportMut.mutate()}
              loading={exportMut.isPending}
              disabled={total === 0}
              title={
                total === 0
                  ? "Add at least one section before exporting"
                  : "Generate a DOCX with all sections in order"
              }
            >
              Export DOCX
            </Button>
          </div>
        }
      />

      <div className="p-8 pb-24 space-y-6 max-w-4xl">
        {/* "How drafts work" intro card — only when there are no sections yet */}
        {items.length === 0 && (
          <Card>
            <div className="flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
              >
                <Info size={16} style={{ color: "#F97316" }} />
              </div>
              <div className="text-xs flex-1" style={{ color: "#9CA3AF" }}>
                <p className="text-white text-sm font-medium mb-1">How Drafts work</p>
                <p>
                  A response is composed of sections — narrative parts the team writes,
                  designer-produced visuals, pricing, attachments, CVs. Each section has a kind that
                  determines how it's authored. AI can suggest a starter outline based on the RFP
                  and the format you picked, or you can add sections manually.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Action bar */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="primary"
            size="sm"
            icon={<Sparkles size={14} />}
            onClick={handleSuggestOutline}
            loading={suggestOutline.isPending}
          >
            {items.length === 0 ? "Suggest outline" : "Suggest more sections"}
          </Button>

          <Button
            variant="secondary"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => setShowAddForm(true)}
          >
            Add section manually
          </Button>

          <div className="flex-1" />

          {total > 0 && (
            <span className="text-xs" style={{ color: "#9CA3AF" }}>
              {total} section{total === 1 ? "" : "s"}
            </span>
          )}
        </div>

        {/* Add-section form (collapsible) */}
        {showAddForm && (
          <AddSectionForm
            projectId={projectId}
            onSubmit={async (body) => {
              try {
                await createSection.mutateAsync(body);
                toast.success("Section added.");
                setShowAddForm(false);
              } catch (err) {
                toast.error((err as Error).message);
              }
            }}
            onCancel={() => setShowAddForm(false)}
            submitting={createSection.isPending}
          />
        )}

        {/* Composition status — what's left before export is meaningful */}
        {items.length > 0 && (
          <CompositionStatus
            total={total}
            completed={completed}
            missingDraft={missingDraft}
            missingAsset={missingAsset}
            readyForExport={readyForExport}
            deliverableCovered={coverage.data?.deliverableCovered ?? null}
            deliverableTotal={coverage.data?.deliverable ?? null}
          />
        )}

        {/* Sections list */}
        {items.length === 0 ? (
          <Card>
            <div className="text-center py-10">
              <p className="text-sm font-medium text-white">No sections yet.</p>
              <p className="text-xs mt-1" style={{ color: "#6B7280" }}>
                Click <span className="text-orange-300">Suggest outline</span> to let AI propose the
                response structure, or add sections manually.
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-2">
            {items.map((section, idx) => (
              <SectionRow
                key={section.id}
                section={section}
                projectId={projectId}
                index={idx}
                onDelete={() => setPendingDelete(section)}
              />
            ))}
          </div>
        )}
      </div>

      {showFormatPicker && (
        <FormatPickerModal
          current={submissionFormat}
          onPick={handleFormatPick}
          onClose={() => setShowFormatPicker(false)}
          submitting={updateProject.isPending}
        />
      )}

      {showOutlineModal && (
        <OutlineModal
          loading={suggestOutline.isPending}
          proposals={outlineProposals}
          applying={applyOutline.isPending}
          onClose={() => {
            if (applyOutline.isPending) return;
            setShowOutlineModal(false);
            setOutlineProposals(null);
          }}
          onApply={(chosen) => handleApplyOutline(chosen)}
        />
      )}

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title="Delete this section?"
        description={
          pendingDelete
            ? `"${pendingDelete.title}" will be removed from the response. Drafts written in it will be lost.`
            : ""
        }
        confirmLabel="Delete"
        onConfirm={async () => {
          if (!pendingDelete) return;
          try {
            await deleteSection.mutateAsync(pendingDelete.id);
            toast.success("Section deleted.");
            setPendingDelete(null);
          } catch (err) {
            toast.error((err as Error).message);
          }
        }}
        busy={deleteSection.isPending}
      />

      <NextStepFooter currentStep="drafts" projectId={projectId} />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Helpers + child components
// ---------------------------------------------------------------------------

function formatLabel(f: SubmissionFormat): string {
  return f === "doc" ? "Word document" : f === "presentation" ? "Presentation" : "Both";
}

/**
 * Full-page format chooser shown when the project has no submissionFormat
 * yet. The user can't proceed past this until a choice is made — by design.
 */
function FormatChoicePage({
  onPick,
  submitting,
}: {
  onPick: (f: SubmissionFormat) => void;
  submitting: boolean;
}) {
  return (
    <div className="p-8 pb-24 max-w-2xl">
      <Card>
        <div className="flex items-start gap-3 mb-5">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
          >
            <Sparkles size={18} style={{ color: "#F97316" }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-white">
              How will the response be submitted?
            </h2>
            <p className="text-xs mt-1 leading-relaxed" style={{ color: "#9CA3AF" }}>
              Pick the format the client expects so the section structure, AI prompts, and export
              are tuned to it. Most RFPs require a written submission. Decks are usually for the
              oral presentation phase, after shortlisting. You can change this from the badge in the
              page header anytime.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2">
          <FormatChoice
            format="doc"
            label="Word document"
            icon={FileText}
            description="Written proposal — most common in government and emerging-market RFPs"
            recommended
            current={null}
            onPick={onPick}
            disabled={submitting}
          />
          <FormatChoice
            format="presentation"
            label="Presentation"
            icon={Presentation}
            description="Slide deck — usually for the oral pitch after shortlisting"
            current={null}
            onPick={onPick}
            disabled={submitting}
          />
          <FormatChoice
            format="both"
            label="Both — doc + deck"
            icon={LayoutIcon}
            description="Submission is a written doc; an oral presentation comes later"
            current={null}
            onPick={onPick}
            disabled={submitting}
          />
        </div>
      </Card>
    </div>
  );
}

/**
 * Top-of-page status card. Shows readiness for export at a glance: how
 * many sections are final, how many drafts are missing prose, how many
 * asset-backed sections are still empty. Drives the "what's left" feel.
 */
function CompositionStatus({
  total,
  completed,
  missingDraft,
  missingAsset,
  readyForExport,
  deliverableCovered,
  deliverableTotal,
}: {
  total: number;
  completed: number;
  missingDraft: number;
  missingAsset: number;
  readyForExport: boolean;
  deliverableCovered: number | null;
  deliverableTotal: number | null;
}) {
  const progressPct = total === 0 ? 0 : Math.round((completed / total) * 100);
  const dotColor = readyForExport ? "#22c55e" : "#F97316";
  return (
    <Card>
      <div className="flex items-start gap-3">
        <span
          className="w-2 h-2 rounded-full flex-shrink-0 mt-2"
          style={{ backgroundColor: dotColor }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <p className="text-sm font-semibold text-white">
              {readyForExport ? "Response is ready to export" : "Response in progress"}
            </p>
            <span className="text-xs" style={{ color: "#9CA3AF" }}>
              {completed} of {total} final · {progressPct}%
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap mt-2 text-xs">
            <CompStat
              count={missingDraft}
              label={missingDraft === 1 ? "draft missing prose" : "drafts missing prose"}
              tone={missingDraft === 0 ? "ok" : "warn"}
            />
            <CompStat
              count={missingAsset}
              label={missingAsset === 1 ? "asset slot empty" : "asset slots empty"}
              tone={missingAsset === 0 ? "ok" : "warn"}
            />
            {/* Cross-link coverage — only meaningful when there are deliverable
                requirements parsed for this project. */}
            {deliverableTotal !== null && deliverableCovered !== null && deliverableTotal > 0 && (
              <CompStat
                count={deliverableTotal - deliverableCovered}
                label={
                  deliverableTotal - deliverableCovered === 1
                    ? "requirement uncovered"
                    : "requirements uncovered"
                }
                tone={deliverableTotal === deliverableCovered ? "ok" : "warn"}
              />
            )}
          </div>

          <p className="text-[11px] mt-2.5" style={{ color: "#6B7280" }}>
            Click Export DOCX in the page header to assemble all sections in order. Empty sections
            render as italic [Draft pending] placeholders so gaps are visible in the file.
          </p>
        </div>
      </div>

      <div
        className="h-1 mt-3 rounded-full overflow-hidden"
        style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
      >
        <div
          className="h-full transition-all"
          style={{
            width: `${progressPct}%`,
            backgroundColor: readyForExport ? "#22c55e" : "#F97316",
          }}
        />
      </div>
    </Card>
  );
}

function CompStat({ count, label, tone }: { count: number; label: string; tone: "ok" | "warn" }) {
  if (tone === "ok" && count === 0) {
    return (
      <span className="inline-flex items-center gap-1.5" style={{ color: "#22c55e" }}>
        <CheckCircle2 size={11} />
        Nothing missing
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5"
      style={{ color: tone === "warn" ? "#F97316" : "#9CA3AF" }}
    >
      {tone === "warn" ? <AlertTriangle size={11} /> : <CheckCircle2 size={11} />}
      {count} {label}
    </span>
  );
}

function SubmissionFormatBadge({
  format,
  onChange,
}: {
  format: SubmissionFormat | null;
  onChange: () => void;
}) {
  const Icon = format === "presentation" ? Presentation : format === "both" ? LayoutIcon : FileText;
  return (
    <button
      onClick={onChange}
      className="inline-flex items-center gap-2 text-xs text-gray-300 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-white/5 border border-white/10"
      title="Change submission format"
    >
      <Icon size={12} />
      Format: {format ? formatLabel(format) : "Not set"}
    </button>
  );
}

function SectionRow({
  section,
  projectId,
  index,
  onDelete,
}: {
  section: ProposalSection;
  projectId: string;
  index: number;
  onDelete: () => void;
}) {
  const meta = KIND_META[section.kind];
  const Icon = meta.icon;
  const statusColor =
    section.status === "complete"
      ? "#22c55e"
      : section.status === "draft_ready" || section.status === "in_review"
        ? "#F97316"
        : section.status === "in_progress"
          ? "#60A5FA"
          : "#6B7280";
  const statusLabel =
    section.status === "complete"
      ? "Final"
      : section.status === "in_review"
        ? "In review"
        : section.status === "draft_ready"
          ? "Draft ready"
          : section.status === "in_progress"
            ? "In progress"
            : "Not started";

  return (
    <Link href={`/projects/${projectId}/sections/${section.id}`}>
      <button
        className="w-full text-left rounded-xl border px-4 py-3 flex items-center gap-3 hover:bg-white/[0.02] transition-colors"
        style={{
          backgroundColor: "#161B22",
          borderColor: "rgba(229,231,235,0.08)",
        }}
      >
        <span
          className="text-[10px] font-mono w-6 text-right flex-shrink-0"
          style={{ color: "#6B7280" }}
        >
          {String(index + 1).padStart(2, "0")}
        </span>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
        >
          <Icon size={14} style={{ color: "#9CA3AF" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate flex items-center gap-1.5">
            {section.title}
            {section.sensitive && (
              <Lock
                size={11}
                style={{ color: "#F59E0B" }}
                className="flex-shrink-0"
                aria-label="Sensitive — restricted to admin/finance and approved viewers"
              />
            )}
          </p>
          <p className="text-xs mt-0.5" style={{ color: "#6B7280" }}>
            {meta.label}
            {section.sensitive && (
              <span style={{ color: "#F59E0B" }} className="ml-2">
                · Sensitive
              </span>
            )}
          </p>
        </div>
        <span
          className="text-[10px] uppercase tracking-wider font-bold flex-shrink-0 px-2 py-0.5 rounded-full"
          style={{
            color: statusColor,
            backgroundColor: "rgba(255,255,255,0.04)",
          }}
        >
          {statusLabel}
        </span>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }}
          className="text-gray-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-500/10 flex-shrink-0"
          title="Delete section"
        >
          <Trash2 size={13} />
        </button>
        <ChevronRight size={14} className="text-gray-500 flex-shrink-0" />
      </button>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Submission format picker modal
// ---------------------------------------------------------------------------

function FormatPickerModal({
  current,
  onPick,
  onClose,
  submitting,
}: {
  current: SubmissionFormat | null;
  onPick: (f: SubmissionFormat) => void;
  onClose: () => void;
  submitting: boolean;
}) {
  return (
    <ModalShell onClose={onClose}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="text-sm font-semibold text-white">How will the response be submitted?</h3>
          <p className="text-xs mt-1" style={{ color: "#9CA3AF" }}>
            Most RFPs require a written submission. Decks come in for the oral presentation phase,
            after shortlisting. You can change this any time from the badge in the page header.
          </p>
        </div>
        <button
          onClick={onClose}
          disabled={submitting}
          className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5 flex-shrink-0"
        >
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2 mt-4">
        <FormatChoice
          format="doc"
          label="Word document"
          icon={FileText}
          description="Written proposal — most common in government and emerging-market RFPs"
          recommended
          current={current}
          onPick={onPick}
          disabled={submitting}
        />
        <FormatChoice
          format="presentation"
          label="Presentation"
          icon={Presentation}
          description="Slide deck — usually for the oral pitch after shortlisting"
          current={current}
          onPick={onPick}
          disabled={submitting}
        />
        <FormatChoice
          format="both"
          label="Both — doc + deck"
          icon={LayoutIcon}
          description="Submission is a written doc; an oral presentation comes later"
          current={current}
          onPick={onPick}
          disabled={submitting}
        />
      </div>
    </ModalShell>
  );
}

function FormatChoice({
  format,
  label,
  icon: Icon,
  description,
  recommended,
  current,
  onPick,
  disabled,
}: {
  format: SubmissionFormat;
  label: string;
  icon: React.ElementType;
  description: string;
  recommended?: boolean;
  current: SubmissionFormat | null;
  onPick: (f: SubmissionFormat) => void;
  disabled: boolean;
}) {
  const isCurrent = current === format;
  return (
    <button
      onClick={() => onPick(format)}
      disabled={disabled}
      className="text-left rounded-lg border px-3 py-3 flex items-start gap-3 hover:bg-white/[0.04] transition-colors disabled:opacity-50"
      style={{
        backgroundColor: isCurrent ? "rgba(249,115,22,0.08)" : "rgba(255,255,255,0.02)",
        borderColor: isCurrent ? "rgba(249,115,22,0.3)" : "rgba(229,231,235,0.08)",
      }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
      >
        <Icon size={16} className="text-gray-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white inline-flex items-center gap-2">
          {label}
          {recommended && (
            <span
              className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full"
              style={{
                color: "#F97316",
                backgroundColor: "rgba(249,115,22,0.1)",
              }}
            >
              Recommended
            </span>
          )}
        </p>
        <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
          {description}
        </p>
      </div>
      {isCurrent && (
        <CheckCircle2 size={14} className="flex-shrink-0 mt-1.5" style={{ color: "#F97316" }} />
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Outline suggestion modal
// ---------------------------------------------------------------------------

/**
 * Editable working copy of an outline row. The AI's `rationale` becomes the
 * `purpose` once the row is saved (rationale = AI's reason, purpose = the
 * shaped intent the team uses going forward).
 */
interface OutlineDraftRow {
  title: string;
  kind: SectionKind;
  purpose: string;
  /** True if user wants this row in the final outline. */
  accepted: boolean;
  /** Distinguishes AI-proposed rows from rows the user added in the modal. */
  origin: "ai" | "manual";
}

function OutlineModal({
  loading,
  proposals,
  applying,
  onClose,
  onApply,
}: {
  loading: boolean;
  proposals: OutlineSuggestion[] | null;
  applying: boolean;
  onClose: () => void;
  onApply: (chosen: Array<{ title: string; kind: SectionKind; purpose?: string }>) => void;
}) {
  // Editable working copy. Hydrated from AI proposals, but the user can edit,
  // add, or remove any row before approving.
  const [rows, setRows] = useState<OutlineDraftRow[]>([]);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Track the original AI shape so we can detect "dirty" state.
  const originalSnapshot = useMemo(() => {
    if (!proposals) return "";
    return JSON.stringify(
      proposals.map((p) => ({
        title: p.title,
        kind: p.kind,
        purpose: p.rationale,
      })),
    );
  }, [proposals]);

  const currentSnapshot = JSON.stringify(
    rows
      .filter((r) => r.accepted)
      .map((r) => ({ title: r.title, kind: r.kind, purpose: r.purpose })),
  );
  const dirty = currentSnapshot !== originalSnapshot && rows.length > 0;

  // Hydrate when proposals arrive.
  useEffect(() => {
    if (!proposals) return;
    setRows(
      proposals.map((p) => ({
        title: p.title,
        kind: p.kind,
        purpose: p.rationale,
        accepted: true,
        origin: "ai" as const,
      })),
    );
  }, [proposals]);

  // Esc closes (with confirm if dirty).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !applying) handleClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applying, dirty]);

  const handleClose = () => {
    if (dirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const updateRow = (idx: number, patch: Partial<OutlineDraftRow>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const removeRow = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const addRow = () =>
    setRows((prev) => [
      ...prev,
      {
        title: "",
        kind: "prose_ai",
        purpose: "",
        accepted: true,
        origin: "manual" as const,
      },
    ]);

  const handleApprove = () => {
    const chosen = rows.filter((r) => r.accepted && r.title.trim().length > 0);
    if (chosen.length === 0) {
      toast.info("Add at least one section before approving.");
      return;
    }
    onApply(
      chosen.map((r) => ({
        title: r.title.trim(),
        kind: r.kind,
        purpose: r.purpose.trim(),
      })),
    );
  };

  const acceptedCount = rows.filter((r) => r.accepted).length;

  return (
    <>
      <ModalShell onClose={handleClose} maxWidth={760}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="text-sm font-semibold text-white inline-flex items-center gap-2">
              <Sparkles size={14} style={{ color: "#F97316" }} /> AI-suggested outline
            </h3>
            <p className="text-xs mt-1" style={{ color: "#9CA3AF" }}>
              Edit any title, kind, or purpose. Add new sections at the bottom. Uncheck rows you
              don't want. When you approve, the chosen sections become the spine of your response —
              they're what you'll write, generate, or upload against.
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={applying}
            className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5 flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-4 max-h-[60vh] overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-8 justify-center">
              <Loader2 size={14} className="animate-spin" />
              Building outline…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm py-8 text-center" style={{ color: "#9CA3AF" }}>
              No proposals. Add one below.
            </p>
          ) : (
            rows.map((r, idx) => (
              <OutlineDraftRowCard
                key={idx}
                index={idx}
                row={r}
                disabled={applying}
                onChange={(patch) => updateRow(idx, patch)}
                onRemove={() => removeRow(idx)}
              />
            ))
          )}

          {!loading && (
            <button
              onClick={addRow}
              disabled={applying}
              className="w-full rounded-lg border border-dashed px-3 py-3 text-xs text-gray-300 hover:text-white hover:bg-white/[0.04] inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
              style={{ borderColor: "rgba(229,231,235,0.15)" }}
            >
              <Plus size={12} />
              Add a section
            </button>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-xs" style={{ color: "#9CA3AF" }}>
              {acceptedCount} of {rows.length} selected
            </p>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleClose} disabled={applying}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon={<CheckCircle2 size={14} />}
                onClick={handleApprove}
                loading={applying}
                disabled={acceptedCount === 0}
              >
                Approve outline
              </Button>
            </div>
          </div>
        )}
      </ModalShell>

      <ConfirmDeleteDialog
        open={showCloseConfirm}
        onOpenChange={(o) => setShowCloseConfirm(o)}
        title="Discard outline edits?"
        description="You've made changes to the AI's suggested outline. Closing now will lose them. The next time you click Suggest outline, you'll get a fresh proposal."
        confirmLabel="Discard"
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
      />
    </>
  );
}

/**
 * One editable row in the outline modal. Title is a single-line input.
 * Purpose is a small textarea. Kind is a dropdown of all SECTION_KINDS.
 * The row is "accepted" via the leading checkbox; unchecked rows are
 * dimmed but not removed (so the user can flip back).
 */
function OutlineDraftRowCard({
  index,
  row,
  disabled,
  onChange,
  onRemove,
}: {
  index: number;
  row: OutlineDraftRow;
  disabled: boolean;
  onChange: (patch: Partial<OutlineDraftRow>) => void;
  onRemove: () => void;
}) {
  const meta = KIND_META[row.kind];
  const Icon = meta.icon;

  return (
    <div
      className="rounded-lg border px-3 py-3"
      style={{
        backgroundColor: row.accepted ? "rgba(249,115,22,0.04)" : "rgba(255,255,255,0.02)",
        borderColor: row.accepted ? "rgba(249,115,22,0.2)" : "rgba(229,231,235,0.06)",
        opacity: row.accepted ? 1 : 0.6,
      }}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={row.accepted}
          onChange={(e) => onChange({ accepted: e.target.checked })}
          disabled={disabled}
          className="mt-2 w-3.5 h-3.5 rounded accent-orange-500 cursor-pointer flex-shrink-0"
          title={row.accepted ? "Uncheck to skip this section" : "Check to include"}
        />

        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-1"
          style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
        >
          <Icon size={13} className="text-gray-300" />
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {/* Title input */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={row.title}
              onChange={(e) => onChange({ title: e.target.value })}
              disabled={disabled}
              placeholder="Section title"
              className="flex-1 bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-sm text-white outline-none focus:border-orange-400/30"
            />
            <span
              className="text-[10px] uppercase tracking-wider font-bold flex-shrink-0"
              style={{ color: "#6B7280" }}
              title={row.origin === "ai" ? "Suggested by AI" : "Added manually"}
            >
              {row.origin === "ai" ? "AI" : "Manual"}
            </span>
          </div>

          {/* Kind picker */}
          <div className="flex items-center gap-2">
            <label
              className="text-[10px] uppercase tracking-wider font-bold flex-shrink-0 w-12"
              style={{ color: "#6B7280" }}
            >
              Kind
            </label>
            <select
              value={row.kind}
              onChange={(e) => onChange({ kind: e.target.value as SectionKind })}
              disabled={disabled}
              className="bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-xs text-white outline-none flex-1"
            >
              {(Object.keys(KIND_META) as SectionKind[]).map((k) => (
                <option key={k} value={k} style={{ backgroundColor: "#111318" }}>
                  {KIND_META[k].label}
                </option>
              ))}
            </select>
          </div>

          {/* Purpose textarea */}
          <div className="flex items-start gap-2">
            <label
              className="text-[10px] uppercase tracking-wider font-bold flex-shrink-0 w-12 pt-1"
              style={{ color: "#6B7280" }}
            >
              Purpose
            </label>
            <textarea
              value={row.purpose}
              onChange={(e) => onChange({ purpose: e.target.value })}
              disabled={disabled}
              placeholder="What this section should cover — one or two sentences. AI uses this when drafting."
              rows={2}
              className="flex-1 bg-white/[0.03] border border-white/10 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-orange-400/30 resize-y"
            />
          </div>
        </div>

        <button
          onClick={onRemove}
          disabled={disabled}
          className="text-gray-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-500/10 flex-shrink-0 mt-1"
          title="Remove this row"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-section manual form
// ---------------------------------------------------------------------------

function AddSectionForm({
  onSubmit,
  onCancel,
  submitting,
}: {
  projectId: string;
  onSubmit: (body: { title: string; kind: SectionKind }) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<SectionKind>("prose_ai");

  const canSubmit = title.trim().length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ title: title.trim(), kind });
  };

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Add a section</h3>
            <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
              Pick a kind to determine how the section is authored.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/5"
          >
            <X size={14} />
          </button>
        </div>

        <Input
          label="Title"
          placeholder="Executive Summary"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <div>
          <p
            className="text-[10px] uppercase tracking-wider font-bold mb-1.5"
            style={{ color: "#9CA3AF" }}
          >
            Kind
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(KIND_META) as SectionKind[]).map((k) => {
              const meta = KIND_META[k];
              const Icon = meta.icon;
              const isPicked = kind === k;
              return (
                <button
                  type="button"
                  key={k}
                  onClick={() => setKind(k)}
                  className="text-left rounded-lg border px-2.5 py-2 flex items-start gap-2 hover:bg-white/[0.04]"
                  style={{
                    backgroundColor: isPicked ? "rgba(249,115,22,0.06)" : "rgba(255,255,255,0.02)",
                    borderColor: isPicked ? "rgba(249,115,22,0.25)" : "rgba(229,231,235,0.06)",
                  }}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
                  >
                    <Icon size={13} className="text-gray-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{meta.label}</p>
                    <p className="text-[10px] mt-0.5 line-clamp-2" style={{ color: "#9CA3AF" }}>
                      {meta.blurb}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onCancel} type="button" disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            icon={<Plus size={14} />}
            disabled={!canSubmit}
            loading={submitting}
          >
            Add section
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Modal shell
// ---------------------------------------------------------------------------

function ModalShell({
  children,
  onClose,
  maxWidth = 480,
}: {
  children: React.ReactNode;
  onClose: () => void;
  maxWidth?: number;
}) {
  return (
    <>
      <div
        className="fixed inset-0 z-30"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-40 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="rounded-xl border w-full pointer-events-auto"
          style={{
            backgroundColor: "#0F1419",
            borderColor: "rgba(229,231,235,0.08)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
            maxWidth,
            maxHeight: "85vh",
          }}
        >
          <div className="px-5 py-5">{children}</div>
        </div>
      </div>
    </>
  );
}
